<template>
  <div
    class="Custom-Reset"
    :class="{ 'Custom-Reset--active': isPulling, 'Custom-Reset--released': isRefreshing }"
    ref="pullEl"
  >
    <div
      class="Custom-Reset__spinner"
      :class="{ 'Custom-Reset__spinner--refreshing': isRefreshing }"
      :style="{ transform: isRefreshing ? 'rotate(0deg)' : `rotate(${spinnerRotation}deg)` }"
    >
      <svg
        class="Custom-Reset__arrow"
        :style="{
          opacity: arrowOpacity,
          transform: `rotate(${arrowRotation}deg)`,
          transformOrigin: 'center',
          fill: arrowColor
        }"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g transform="translate(0, -1)">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M13.7071 1.29289C14.0976 1.68342 14.0976 2.31658 13.7071 2.70711L12.4053 4.00896C17.1877 4.22089 21 8.16524 21 13C21 17.9706 16.9706 22 12 22C7.02944 22 3 17.9706 3 13C3 12.4477 3.44772 12 4 12C4.55228 12 5 12.4477 5 13C5 16.866 8.13401 20 12 20C15.866 20 19 16.866 19 13C19 9.2774 16.0942 6.23349 12.427 6.01281L13.7071 7.29289C14.0976 7.68342 14.0976 8.31658 13.7071 8.70711C13.3166 9.09763 12.6834 9.09763 12.2929 8.70711L9.29289 5.70711C9.10536 5.51957 9 5.26522 9 5C9 4.73478 9.10536 4.48043 9.29289 4.29289L12.2929 1.29289C12.6834 0.902369 13.3166 0.902369 13.7071 1.29289Z"
          />
        </g>
      </svg>
    </div>
  </div>
</template>

<script setup>
  import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
  import { useStore } from 'vuex';
  import DROPDOWN_NAMES from '@shared/constants/dropdowns/dropdownsName';

  const store = useStore();

  const maxPull = 140;
  const minSwipeDistance = 20;
  const spinDuration = 1000;
  const pullEffort = 0.7;

  const isPulling = ref(false);
  const isRefreshing = ref(false);
  const touchStartY = ref(0);
  const touchStartX = ref(0);
  const pullDistance = ref(0);
  const pullEl = ref(null);
  const isEligibleStart = ref(false);
  const directionLocked = ref(false);
  const spinAngle = ref(0);
  const spinAnimation = ref(null);

  // Визуальные эффекты
  const arrowOpacity = computed(() => Math.min(1, 0.4 + (pullDistance.value / maxPull) * 0.6));
  const arrowRotation = computed(() => Math.min(180, (pullDistance.value / maxPull) * 180));
  const arrowColor = computed(() => (pullDistance.value >= maxPull ? '#2563EB' : '#999999'));
  
  const spinnerRotation = computed(() => {
    if (isRefreshing.value) return spinAngle.value;
    return Math.min(180, (pullDistance.value / maxPull) * 180);
  });

  const startSpinning = () => {
    let startTime = null;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;

      spinAngle.value = (progress / spinDuration) * 360;

      if (progress < spinDuration) {
        spinAnimation.value = requestAnimationFrame(animate);
      } else {
        startTime = null;
        spinAnimation.value = requestAnimationFrame(animate);
      }
    };

    spinAnimation.value = requestAnimationFrame(animate);
  };

  const stopSpinning = () => {
    if (spinAnimation.value) {
      cancelAnimationFrame(spinAnimation.value);
      spinAnimation.value = null;
    }
  };

  const reset = () => {
    pullDistance.value = 0;
    if (pullEl.value) {
      pullEl.value.style.transition = 'top 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)';
      pullEl.value.style.top = `-60px`;
      setTimeout(() => {
        if (pullEl.value) pullEl.value.style.transition = '';
      }, 300);
    }
  };

  const doRefresh = async () => {
    startSpinning();
    setTimeout(() => {
      window.location.reload();
    }, 300);
  };

  const applyResistance = (distance) => {
    const pullRatio = Math.min(distance / maxPull, 1);
    const effortFactor = Math.min(Math.max(0.1, pullEffort), 2);
    return Math.pow(pullRatio, 1 / effortFactor) * maxPull;
  };

  const onTouchStart = (e) => {
    const isContentPopup = store.getters['Dropdowns/isInList'](DROPDOWN_NAMES.contentPage);
    if (isRefreshing.value) return;
    if (
      store.getters['Dropdowns/isOpenedDropdown'] &&
      (!isContentPopup) 
    ) return;
    let scrollY = 0;
    if(isContentPopup) scrollY = document.querySelector('[data-content-scroll-wrapper]').scrollTop;
    else scrollY = window.scrollY;
    isEligibleStart.value =scrollY === 0;
    if (!isEligibleStart.value) return;
    touchStartY.value = e.touches[0].clientY;
    touchStartX.value = e.touches[0].clientX;
    directionLocked.value = false;
    isPulling.value = true;

    if (pullEl.value) {
      pullEl.value.style.transition = 'none';
      pullEl.value.style.willChange = 'top';
    }
  };

  const onTouchMove = (e) => {
    if (!isPulling.value || !isEligibleStart.value) return;

    const deltaY = e.touches[0].clientY - touchStartY.value;
    const deltaX = e.touches[0].clientX - touchStartX.value;

    if (!directionLocked.value) {
      if (Math.abs(deltaX) > Math.abs(deltaY) || deltaY < 0) {
        isPulling.value = false;
        isEligibleStart.value = false;
        return;
      }
      directionLocked.value = true;
    }

    const easedDistance = deltaY < minSwipeDistance
      ? deltaY * (deltaY / minSwipeDistance / 2.2)
      : deltaY / 1.8;

    const resistedDistance = applyResistance(easedDistance);
    pullDistance.value = resistedDistance;

    if (pullEl.value) {
      pullEl.value.style.top = `${resistedDistance - 60}px`;
    }

    e.preventDefault();
  };

  const onTouchEnd = async () => {
    if (!isPulling.value || !isEligibleStart.value) return;

    isPulling.value = false;
    isEligibleStart.value = false;
    directionLocked.value = false;

    if (pullDistance.value >= maxPull) {
      isRefreshing.value = true;
      if (pullEl.value) {
        pullEl.value.style.transition = 'top 0.15s cubic-bezier(0.25, 0.1, 0.25, 1)';
      }
      await doRefresh();
    } else {
      reset();
    }
  };

  const onScroll = () => {
    if (window.scrollY > 0 && !isRefreshing.value && pullEl.value) {
      pullEl.value.style.transition = 'top 0.15s cubic-bezier(0.25, 0.1, 0.25, 1)';
      pullEl.value.style.top = `-60px`;
    }
  };

  onMounted(() => {
    window.addEventListener('touchstart', onTouchStart, { passive: false });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('scroll', onScroll);
  });

  onBeforeUnmount(() => {
    stopSpinning();
    window.removeEventListener('touchstart', onTouchStart);
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('touchend', onTouchEnd);
    window.removeEventListener('scroll', onScroll);
  });
</script>

<style scoped lang="scss">
  @import './styles.scss';
</style>